<script lang="ts" setup>
    import { ref, inject } from 'vue';
    import { removeItemFromList, changeOrderOfListItems } from './FormEditor';
    import BSFormInputText from '@shared/components/BSFormInputText.vue';
    import InputCheckbox from '@shared/components/InputCheckbox.vue';
    import type { FormField, FormFieldsReference, inputTypes } from './FormEditor.types';
    import Collapse from '@shared/components/Collapse.vue';
    import { AccountAccess } from '@cwd/Account/Account.access';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/bsAlerts';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const accountAccess: AccountAccess = injectorAccess.injector.get(AccountAccess);

    const props = defineProps<{
        formFields: FormFieldsReference;
    }>();

    const allInputTypes: inputTypes[] = ['text', 'radio', 'select', 'checkbox', 'date', 'number', 'hidden'];

    const isBoolField = (contactField: string) => contactField.startsWith('CustomBool');
    const isDateField = (contactField: string) => contactField.startsWith('CustomDate');
    const isNumericField = (contactField: string) => contactField.startsWith('CustomNumeric');

    const availableInputTypes = (contactField: string) => {
        if (isBoolField(contactField)) return ['boolean', 'hidden'] as inputTypes[];
        if (isDateField(contactField)) return ['date', 'hidden'] as inputTypes[];
        if (isNumericField(contactField)) return ['number', 'hidden'] as inputTypes[];
        return allInputTypes;
    };

    let toggleIndex = ref<null | number>();

    const ensureBoolOptions = (formField: FormField) => {
        if (isBoolField(formField.contactField) && formField.type === 'boolean' && (!formField.options || formField.options.length === 0)) {
            formField.options = [
                { label: $Resources.BooleanYesLabel, value: 'true' },
                { label: $Resources.BooleanNoLabel, value: 'false' },
            ];
        }
    };

    props.formFields.forEach(ensureBoolOptions);

    const displayName = (contactField: string, resources: any) => {
        const customFields: any = accountAccess.state.currentAccount!.CustomField;

        if (customFields) {
            const key = contactField.startsWith('Custom') && !contactField.match(/^Custom(Date|Bool|Numeric|Long)/)
                ? contactField.replace('Custom', 'CustomField')
                : contactField;
            const customName = customFields[key];
            if (customName?.trim()) return customName;
        }

        return (
            resources['Contact' + contactField + 'Label'] ||
            resources['ContactModal' + contactField] ||
            contactField
        );
    };

    const addOption = (formField: FormField) => {
        if (!formField.options) {
            formField.options = [];
        }
        formField.options.push({ label: '', value: '' });
    };

    /*
    $Resources.InputTypeText
    $Resources.InputTypeNumber
    $Resources.InputTypeDate
    $Resources.InputTypeRadio
    $Resources.InputTypeSelect
    $Resources.InputTypeHidden
    $Resources.InputTypeCheckbox
    $Resources.InputTypeBoolean
    */
</script>

<template>
    <h2 class="h3">{{ $Resources.SubsciptionFormsSubscriberInputsHeading }}</h2>
    <TransitionGroup tag="ul" name="list" class="list-unstyled list-border-divider list-item-state-hover list-hover m-neg-lg-r m-neg-lg-l m-lg-b">
        <li class="p-sm-t p-lg-r p-lg-l" v-for="(formField, index) in props.formFields" :key="formField.contactField">
            <div class="d-flex align-items-center">
                <h3 class="h4 m-0-t">{{ displayName(formField.contactField, $Resources) }}</h3>
                <div class="m-sm-b m-auto-l text-nowrap">
                    <button
                        type="button"
                        class="btn btn-subtle btn-sm"
                        :disabled="index > 0 ? false : true"
                        @click="
                            () => {
                                toggleIndex = null;
                                changeOrderOfListItems(props.formFields, index, index - 1);
                            }
                        ">
                        <i class="fa fa-arrow-up text-md" aria-hidden="true"></i>
                        <span class="sr-only">{{ $Resources.FormEditorFieldsAddedUpBtn }}</span>
                    </button>
                    <button
                        type="button"
                        class="btn btn-subtle btn-sm"
                        :disabled="index + 1 !== props.formFields.length ? false : true"
                        @click="
                            () => {
                                toggleIndex = null;
                                changeOrderOfListItems(props.formFields, index, index + 1);
                            }
                        ">
                        <i class="fa fa-arrow-down text-md" aria-hidden="true"></i>
                        <span class="sr-only">{{ $Resources.FormEditorFieldsAddedDownBtn }}</span>
                    </button>
                    <button type="button" class="btn btn-subtle btn-sm" @click="() => (toggleIndex !== index ? (toggleIndex = index) : (toggleIndex = null))">
                        {{ $Resources.FormEditorFieldsAddedEditBtn }}
                    </button>
                    <button
                        type="button"
                        class="btn btn-subtle btn-sm"
                        @click="removeItemFromList(props.formFields, index)"
                        :disabled="formField.contactField === 'Email'">
                        {{ $Resources.FormEditorFieldsAddedRemoveBtn }}
                    </button>
                </div>
            </div>

            <Collapse :open="toggleIndex === index">
                <div v-show="formField.type !== 'email' && formField.type !== 'tel'">
                    <h4 class="h5 m-0-t label-look">{{ $Resources.SelectInputTypeHeading }}</h4>
                    <div class="d-flex flex-wrap gap-xs m-lg-b" role="group">
                        <template v-for="type in availableInputTypes(formField.contactField)">
                            <button
                                type="button"
                                class="btn btn-default btn-sm"
                                :class="{ 'btn-primary': formField.type === type }"
                                @click="() => { (formField.type = type); ensureBoolOptions(formField); }">
                                {{ $Resources['InputType' + type.charAt(0).toUpperCase() + type.slice(1)] }}
                            </button>
                        </template>
                    </div>
                </div>

                <div v-show="formField.type === 'checkbox' && !isBoolField(formField.contactField)">
                    <BsAlert :type="AlertTypes.Info" :content="$Resources.FormEditorCheckboxAlert" :dismissible="false"></BsAlert>
                </div>

                <div v-show="formField.type === 'hidden'">
                    <BSFormInputText class="form-group" v-model="formField.value" :label="$Resources.FormEditorFieldAddedValueLabel"></BSFormInputText>
                </div>

                <div v-show="formField.type !== 'hidden'">
                    <BSFormInputText class="form-group" v-model="formField.label" :label="$Resources.FormEditorFieldAddedLabelLabel"></BSFormInputText>

                    <BSFormInputText
                        class="form-group"
                        v-model="formField.description"
                        :label="$Resources.FormEditorFieldAddedDescriptionLabel"></BSFormInputText>

                    <BSFormInputText
                        class="form-group"
                        v-if="formField.type !== 'radio' && formField.type !== 'date' && formField.type !== 'checkbox' && formField.type !== 'boolean'"
                        v-model="formField.placeholder"
                        :label="$Resources.FormEditorFieldAddedPlaceholderLabel"></BSFormInputText>

                    <div class="form-group" v-show="formField.contactField !== 'Email'">
                        <InputCheckbox :checked="formField.required" @update:checked="(newValue: boolean) => (formField.required = newValue)">
                            <template #label>{{ $Resources.FormEditorFieldAddedRequiredLabel }}</template>
                        </InputCheckbox>
                    </div>

                    <div v-if="formField.type === 'boolean' && isBoolField(formField.contactField)">
                        <h5>{{ $Resources.OptionsHeading }}</h5>
                        <BSFormInputText
                            class="flex-1"
                            v-model="formField.options![0].label"
                            :label="$Resources.FormEditorBooleanTrueLabel"
                            size="sm" />
                        <BSFormInputText
                            class="flex-1"
                            v-model="formField.options![1].label"
                            :label="$Resources.FormEditorBooleanFalseLabel"
                            size="sm" />
                    </div>

                    <div v-show="(formField.type === 'radio' || formField.type === 'select' || formField.type === 'checkbox') && !isBoolField(formField.contactField)">
                        <h5>{{ $Resources.OptionsHeading }}</h5>

                        <ul class="list-unstyled m-lg-b">
                            <li v-for="(option, index) in formField.options" class="d-flex gap-xs align-items-end m-sm-b">
                                <BSFormInputText
                                    class="flex-1"
                                    v-model="option.label"
                                    @blur="option.value.length === 0 && (option.value = option.label)"
                                    :label="$Resources.FormEditorFieldAddedLabelLabel"
                                    size="sm"></BSFormInputText>
                                <BSFormInputText
                                    class="flex-1"
                                    v-model="option.value"
                                    @blur="option.label.length === 0 && (option.label = option.value)"
                                    :label="$Resources.FormEditorFieldAddedValueLabel"
                                    size="sm"></BSFormInputText>

                                <div class="btn-group">
                                    <button
                                        type="button"
                                        class="btn btn-default btn-sm"
                                        :disabled="index > 0 ? false : true"
                                        :title="$Resources.FormEditorFieldsAddedUpBtn"
                                        @click="
                                            () => {
                                                changeOrderOfListItems(formField.options!, index, index - 1);
                                            }
                                        ">
                                        <i class="fa fa-arrow-up" aria-hidden="true"></i>
                                    </button>
                                    <button
                                        type="button"
                                        class="btn btn-default btn-sm"
                                        :disabled="index + 1 !== formField.options!.length ? false : true"
                                        :title="$Resources.FormEditorFieldsAddedDownBtn"
                                        @click="
                                            () => {
                                                changeOrderOfListItems(formField.options!, index, index + 1);
                                            }
                                        ">
                                        <i class="fa fa-arrow-down" aria-hidden="true"></i>
                                    </button>
                                    <button
                                        type="button"
                                        class="btn btn-default btn-sm"
                                        :title="$Resources.FormEditorFieldsAddedRemoveBtn"
                                        @click="removeItemFromList(formField.options!, index)">
                                        <i class="fa fa-trash" aria-hidden="true"></i>
                                    </button>
                                </div>
                            </li>
                        </ul>

                        <div class="form-group">
                            <button type="button" class="btn btn-default btn-sm" @click="addOption(formField)">
                                <i class="fa fa-plus" aria-hidden="true"></i>
                                {{ $Resources.AddOptionBtn }}
                            </button>
                        </div>
                    </div>
                </div>
            </Collapse>
        </li>
    </TransitionGroup>
</template>

<style></style>

<style scoped></style>
